/**
 * Formatted console summary output.
 *
 * Prints a box-drawing formatted summary to stderr at test run completion.
 * Adapts to data present: e2e-only, API-only, or mixed.
 */

import type { Summary } from '@testrelic/core';
import type { PresentedCounts } from './expected-status.js';

const BOX_WIDTH = 58;

function pad(text: string): string {
  const padding = BOX_WIDTH - 2 - text.length;
  return padding > 0 ? text + ' '.repeat(padding) : text;
}

function line(text: string): string {
  return `\u2502  ${pad(text)}  \u2502\n`;
}

/**
 * Print a formatted console summary to stderr.
 *
 * @param summary - Enriched summary statistics
 * @param outputPath - Path to the JSON output file
 * @param htmlReportPath - Path to the HTML report file
 * @param quiet - When true, suppresses all output
 * @param presented - The status counts as Playwright reads them (see
 *   expected-status.ts). `summary` counts by actual status, because it is the
 *   same object written to report.json and sent to the cloud; without this a
 *   `test.fail()` that failed as expected was printed as a failure.
 */
export function printConsoleSummary(
  summary: Summary,
  outputPath: string,
  htmlReportPath: string,
  quiet: boolean,
  presented?: PresentedCounts | null,
): void {
  if (quiet) return;
  if (summary.total === 0) return;

  const top = `\u250C${'─'.repeat(BOX_WIDTH)}\u2510\n`;
  const bottom = `\u2514${'─'.repeat(BOX_WIDTH)}\u2518\n`;
  const blank = line('');

  let output = top;
  output += line('TestRelic AI - Playwright Test Report');
  output += blank;

  // Tests line
  const c = presented ?? summary;
  const parts: string[] = [];
  if (c.passed > 0) parts.push(`${c.passed} \u2713`);
  if (c.failed > 0) parts.push(`${c.failed} \u2717`);
  if (c.flaky > 0) parts.push(`${c.flaky} \u26A0`);
  if (c.skipped > 0) parts.push(`${c.skipped} skipped`);
  if (c.timedout > 0) parts.push(`${c.timedout} timedout`);
  output += line(`Tests:       ${summary.total} total (${parts.join('  ')})`);
  // Say why these counts differ from report.json. One note per line: pad()
  // never truncates, and both notes on one line overran the box's 56 columns.
  if (presented && presented.expectedFailures > 0) {
    output += line(`  ${presented.expectedFailures} failed as expected (test.fail)`);
  }
  if (presented && presented.unexpectedPasses > 0) {
    output += line(`  ${presented.unexpectedPasses} expected to fail, but passed (test.fail)`);
  }

  // Navigation section (only if navigations present)
  if (summary.totalNavigations > 0) {
    output += line(
      `Navigations: ${summary.totalNavigations} visits across ${summary.uniqueNavigationUrls} unique URLs`,
    );
  }

  // API section (only if API calls present)
  if (summary.totalApiCalls > 0) {
    output += line(
      `API Calls:   ${summary.totalApiCalls} calls across ${summary.uniqueApiUrls} unique endpoints`,
    );

    // Method breakdown
    const methodParts = Object.entries(summary.apiCallsByMethod)
      .filter(([, count]) => count > 0)
      .map(([method, count]) => `${method}: ${count}`);
    if (methodParts.length > 0) {
      output += line(`  ${methodParts.join('  ')}`);
    }

    // Status breakdown (only non-zero)
    const statusParts = Object.entries(summary.apiCallsByStatusRange)
      .filter(([, count]) => count > 0)
      .map(([range, count]) => `${range}: ${count}`);
    if (statusParts.length > 0) {
      output += line(`  ${statusParts.join('  ')}`);
    }

    // Response times
    if (summary.apiResponseTime) {
      output += line(
        `  Avg response: ${summary.apiResponseTime.avg}ms  P95: ${summary.apiResponseTime.p95}ms`,
      );
    }
  }

  // Assertion section (only if assertions present)
  if (summary.totalAssertions > 0) {
    output += line(
      `Assertions:  ${summary.totalAssertions} total (${summary.passedAssertions} \u2713  ${summary.failedAssertions} \u2717)`,
    );
  }

  // Action steps section (only if action steps present)
  if (summary.totalActionSteps > 0) {
    const catParts: string[] = [];
    const catMap = summary.actionStepsByCategory;
    if (catMap['ui_action']) catParts.push(`${catMap['ui_action']} UI`);
    if (catMap['assertion']) catParts.push(`${catMap['assertion']} assertions`);
    if (catMap['custom_step']) catParts.push(`${catMap['custom_step']} custom`);
    const catStr = catParts.length > 0 ? ` (${catParts.join('  ')})` : '';
    output += line(`Actions:     ${summary.totalActionSteps} steps${catStr}`);
  }

  // Output paths
  output += line(`Report:      ${htmlReportPath}`);
  output += line(`Data:        ${outputPath}`);

  output += bottom;
  output += 'For more information visit us at https://docs.testrelic.ai\n';

  process.stderr.write(output);
}
